import { type LabelType } from '@/defs/misc'

import { type IconType } from './icon'

/* Colors */
export const COLORS = ['w', 'u', 'b', 'r', 'g', 'c'] as const
export type ColorType = (typeof COLORS)[number]
type ColorLabelType = LabelType<ColorType, { icon: IconType }>
export const COLOR_LABELS: ColorLabelType = {
  b: {
    icon: 'mtg-b',
    label: 'Black',
  },
  c: {
    icon: 'mtg-c',
    label: 'Colorless',
  },
  g: {
    icon: 'mtg-g',
    label: 'Green',
  },
  r: {
    icon: 'mtg-r',
    label: 'Red',
  },
  u: {
    icon: 'mtg-u',
    label: 'Blue',
  },
  w: {
    icon: 'mtg-w',
    label: 'White',
  },
}
export const isColorType = (key?: string): key is ColorType => COLORS.includes(key as ColorType)

/* Color IDs */
export const COLOR_ID_COLORLESS = 'colorless' as const
export const COLOR_ID_MONOS = ['mono-white', 'mono-blue', 'mono-black', 'mono-red', 'mono-green'] as const
export const COLOR_ID_DUALS = [
  'azorius',
  'dimir',
  'rakdos',
  'gruul',
  'selesnya',
  'orzhov',
  'izzet',
  'golgari',
  'boros',
  'simic',
] as const
export const COLOR_ID_TRIS = [
  'esper',
  'grixis',
  'jund',
  'naya',
  'bant',
  'abzan',
  'jeskai',
  'sultai',
  'mardu',
  'temur',
] as const
export const COLOR_ID_QUADS = ['yore-tiller', 'glint-eye', 'dune-brood', 'ink-treader', 'witch-maw'] as const
export const COLOR_ID_FIVE = 'five-color' as const

export const COLOR_IDS = [
  COLOR_ID_COLORLESS,
  ...COLOR_ID_MONOS,
  ...COLOR_ID_DUALS,
  ...COLOR_ID_TRIS,
  ...COLOR_ID_QUADS,
  COLOR_ID_FIVE,
] as const
export type ColorIdType = (typeof COLOR_IDS)[number]
export const isColorIdType = (key?: string): key is ColorIdType => COLOR_IDS.includes(key as ColorIdType)
type ColorIdLabelType = LabelType<ColorIdType, { colors: ColorType[]; icon: IconType; labelShort?: string }>
export const COLOR_ID_LABELS: ColorIdLabelType = {
  // Colorless
  // eslint-disable-next-line sort/object-properties
  colorless: { colors: ['c'], label: 'Colorless', icon: 'mtg-c' },

  // Solos
  'mono-white': { colors: ['w'], icon: 'mtg-w', label: 'Mono-White', labelShort: 'White' },
  'mono-blue': { colors: ['u'], icon: 'mtg-u', label: 'Mono-Blue', labelShort: 'Blue' },
  'mono-black': { colors: ['b'], icon: 'mtg-b', label: 'Mono-Black', labelShort: 'Black' },
  'mono-red': { colors: ['r'], icon: 'mtg-r', label: 'Mono-Red', labelShort: 'Red' },
  'mono-green': { colors: ['g'], icon: 'mtg-g', label: 'Mono-Green', labelShort: 'Green' },

  // Duals
  azorius: { colors: ['w', 'u'], icon: 'mtg-wu', label: 'Azorius' },
  dimir: { colors: ['u', 'b'], icon: 'mtg-ub', label: 'Dimir' },
  rakdos: { colors: ['b', 'r'], icon: 'mtg-br', label: 'Rakdos' },
  gruul: { colors: ['r', 'g'], icon: 'mtg-rg', label: 'Gruul' },
  selesnya: { colors: ['g', 'w'], icon: 'mtg-gw', label: 'Selesnya' },
  orzhov: { colors: ['w', 'b'], icon: 'mtg-wb', label: 'Orzhov' },
  izzet: { colors: ['u', 'r'], icon: 'mtg-ur', label: 'Izzet' },
  golgari: { colors: ['b', 'g'], icon: 'mtg-bg', label: 'Golgari' },
  boros: { colors: ['r', 'w'], icon: 'mtg-rw', label: 'Boros' },
  simic: { colors: ['g', 'u'], icon: 'mtg-gu', label: 'Simic' },

  // Triples
  esper: { colors: ['w', 'u', 'b'], icon: 'mtg-wub', label: 'Esper' },
  grixis: { colors: ['u', 'b', 'r'], icon: 'mtg-ubr', label: 'Grixis' },
  jund: { colors: ['b', 'r', 'g'], icon: 'mtg-brg', label: 'Jund' },
  naya: { colors: ['r', 'g', 'w'], icon: 'mtg-rgw', label: 'Naya' },
  bant: { colors: ['g', 'w', 'u'], icon: 'mtg-gwu', label: 'Bant' },
  abzan: { colors: ['w', 'b', 'g'], icon: 'mtg-wbg', label: 'Abzan' },
  jeskai: { colors: ['u', 'r', 'w'], icon: 'mtg-urw', label: 'Jeskai' },
  sultai: { colors: ['b', 'g', 'u'], icon: 'mtg-bgu', label: 'Sultai' },
  mardu: { colors: ['r', 'w', 'b'], icon: 'mtg-rwb', label: 'Mardu' },
  temur: { colors: ['g', 'u', 'r'], icon: 'mtg-gur', label: 'Temur' },

  // Quadruples
  'yore-tiller': { colors: ['w', 'u', 'b', 'r'], icon: 'mtg-wubr', label: 'Yore-Tiller' },
  'glint-eye': { colors: ['u', 'b', 'r', 'g'], icon: 'mtg-ubrg', label: 'Glint-Eye' },
  'dune-brood': { colors: ['b', 'r', 'g', 'w'], icon: 'mtg-brgw', label: 'Dune-Brood' },
  'ink-treader': { colors: ['r', 'g', 'w', 'u'], icon: 'mtg-rgwu', label: 'Ink-Treader' },
  'witch-maw': { colors: ['g', 'w', 'u', 'b'], icon: 'mtg-gwub', label: 'Witch-Maw' },

  // Five-color
  'five-color': { colors: ['w', 'u', 'b', 'r', 'g'], icon: 'mtg-wubrg', label: 'Five-Color' },
}

type ColorIdGroupLabelType = {
  cis: ColorIdType[]
  label: string
}
export const COLOR_ID_GROUP_LABELS: ColorIdGroupLabelType[] = [
  {
    cis: [COLOR_ID_COLORLESS, ...COLOR_ID_MONOS],
    label: 'Mono Color',
  },
  {
    cis: [...COLOR_ID_DUALS],
    label: 'Two Color',
  },
  {
    cis: [...COLOR_ID_TRIS],
    label: 'Three Color',
  },
  {
    cis: [...COLOR_ID_QUADS, COLOR_ID_FIVE],
    label: 'Four+ Color',
  },
] as const

/* Card Types */
export const CARDTYPES = [
  'creatures',
  'instants',
  'sorceries',
  'artifacts',
  'equipment',
  'utility-artifacts',
  'mana-artifacts',
  'enchantments',
  'auras',
  'sagas',
  'battles',
  'planeswalkers',
  'lands',
  'utility-lands',
  'color-fixing-lands',
] as const
export type CardtypeType = (typeof CARDTYPES)[number]
type CardtypeLabelType = LabelType<CardtypeType, { icon: IconType }>
export const CARDTYPE_LABELS: CardtypeLabelType = {
  // eslint-disable-next-line sort/object-properties
  creatures: { icon: 'type-creature', label: 'Creatures' },
  instants: { icon: 'type-instant', label: 'Instants' },
  sorceries: { icon: 'type-sorcery', label: 'Sorceries' },
  artifacts: { icon: 'type-artifact', label: 'Artifacts' },
  equipment: { icon: 'type-artifact-outlined', label: 'Equipment' },
  'utility-artifacts': { icon: 'type-artifact-outlined', label: 'Utility Artifacts' },
  'mana-artifacts': { icon: 'type-artifact-outlined', label: 'Mana Artifacts' },
  enchantments: { icon: 'type-enchantment', label: 'Enchantments' },
  auras: { icon: 'type-enchantment-outlined', label: 'Auras' },
  sagas: { icon: 'type-enchantment-outlined', label: 'Sagas' },
  battles: { icon: 'type-battle', label: 'Battles' },
  planeswalkers: { icon: 'type-planeswalker', label: 'Planeswalkers' },
  lands: { icon: 'type-land', label: 'Lands' },
  'utility-lands': { icon: 'type-land-outlined', label: 'Utility Lands' },
  'color-fixing-lands': { icon: 'type-land-outlined', label: 'Color-Fixing Lands' },
}
export const isCardtypeType = (key?: string): key is CardtypeType => CARDTYPES.includes(key as CardtypeType)

/* Rarities */
export const RARITIES = ['common', 'uncommon', 'rare', 'mythic', 'other'] as const
export type RarityType = (typeof RARITIES)[number]
type RarityLabelType = LabelType<RarityType, { icon: IconType }>
export const RARITY_LABELS: RarityLabelType = {
  common: { icon: 'c', label: 'Common' },
  mythic: { icon: 'm', label: 'Mythic' },
  other: { icon: 's', label: 'Other' },
  rare: { icon: 'r', label: 'Rare' },
  uncommon: { icon: 'u', label: 'Uncommon' },
}
export const isRarityType = (key?: string): key is RarityType => RARITIES.includes(key as RarityType)

export const GAME_CHANGERS = [
  'ad-nauseam',
  'ancient-tomb',
  'aura-shards',
  'biorhythm',
  'braids-cabal-minion',
  'chrome-mox',
  'coalition-victory',
  'consecrated-sphinx',
  'crop-rotation',
  'cyclonic-rift',
  'demonic-tutor',
  'drannith-magistrate',
  'enlightened-tutor',
  'farewell',
  'field-of-the-dead',
  'fierce-guardianship',
  'force-of-will',
  'gamble',
  'gifts-ungiven',
  'glacial-chasm',
  'grand-arbiter-augustin-iv',
  'grim-monolith',
  'humility',
  'imperial-seal',
  'intuition',
  'bolass-citadel',
  'gaeas-cradle',
  'jeskas-will',
  'lions-eye-diamond',
  'mana-vault',
  'mishras-workshop',
  'mox-diamond',
  'mystical-tutor',
  'narset-parter-of-veils',
  'natural-order',
  'necropotence',
  'notion-thief',
  'opposition-agent',
  'orcish-bowmasters',
  'panoptic-mirror',
  'rhystic-study',
  'seedborn-muse',
  'serras-sanctum',
  'smothering-tithe',
  'survival-of-the-fittest',
  'teferis-protection',
  'tergrid-god-of-fright',
  'thassas-oracle',
  'the-one-ring',
  'the-tabernacle-at-pendrell-vale',
  'underworld-breach',
  'vampiric-tutor',
  'worldly-tutor',
]
